import { Button } from "@/components/Button/Button";
import { PillButton } from "@/components/Button/PillButton";
import { CloseSvg, RenameSvg } from "@/components/Icon/Icon";
import { CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { CaseReadPublic, useGetCaseApiV1CasesCaseIdGetQuery, useUpdateCaseApiV1CasesCaseIdUpdatePutMutation } from "@/store/baseClientApi";
import { selectCheckedIds } from "@/store/slices/tableSlice";
import { RootState } from "@/store/store";
import { theme } from "@/themes/theme";
import { ensureError, localizeError } from "@/utils/helpers";
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, TextField, Typography, useTheme } from "@mui/material";
import { Fragment, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface RenameModalProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (inputText: string) => void;
  name: string;
}

const RenameModal = ({ open, onClose, onUpdate, name }: RenameModalProps) => {
  const { t } = useTranslation("cases");
  const ref = useRef<HTMLInputElement>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "8px" }}>
        {t("label.renameDesign")}
        <IconButton onClick={onClose} size="small">
          <CloseSvg />
        </IconButton>
      </DialogTitle>
      <Divider />

      {/* Body */}
      <DialogContent>
        <Typography color="text.secondary">{t("label.enterNewCaseName") + ":"}</Typography>
        <TextField inputRef={ref} label={t("label.enterCaseName")} defaultValue={name} variant="outlined" fullWidth sx={{ marginTop: 2 }} />
      </DialogContent>

      <Divider />
      {/* Actions */}
      <DialogActions
        sx={{
          padding: "16px",
          justifyContent: "flex-end",
          paddingTop: "8px",
          display: "flex",
          alignItems: "center"
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ borderRadius: "16px", color: theme.palette.primary.main, backgroundColor: theme.palette.secondary.main }}
        >
          {t("common:button.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => ref.current != null && onUpdate(ref.current.value)}
          sx={{ borderRadius: "16px", backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
        >
          {t("common:button.update")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RenamePillButton = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showModal, setShowModal] = useState<boolean>(false);

  const [renameCaseFunction] = useUpdateCaseApiV1CasesCaseIdUpdatePutMutation();

  const caseIdsToRename = useSelector((state: RootState) => selectCheckedIds(state, CASE_TABLE_STATE_NAME));
  const id = caseIdsToRename.length > 0 ? Number(caseIdsToRename[0]) : undefined;
  const isBeingUploaded = id === undefined || id <= 0;

  const { name } = useGetCaseApiV1CasesCaseIdGetQuery(
    { caseId: id ?? 0 },
    { selectFromResult: ({ data }) => ({ name: data?.name }), skip: id === undefined || id <= 0 }
  );

  const handleRename = async (inputText: string) => {
    if (id === undefined) {
      return;
    }

    await renameCaseFunction({
      caseId: id,
      updateCasePublicRequest: { name: inputText }
    })
      .unwrap()
      .then((response: CaseReadPublic) => {
        if (response.name !== inputText) {
          // Name mismatch, notify the user. TODO: confirm toast messages with David and add to i18n
          toast.error("Name update failed.");
        } else {
          toast.success("Case updated successfully!");
        }
      })
      .catch((err) => {
        const error = ensureError(err);
        toast.error(localizeError(t, error));
      });
    setShowModal(false);
  };

  if (name === undefined) {
    return <></>;
  }

  return (
    <Fragment>
      <PillButton
        disabled={caseIdsToRename.length > 1 || isBeingUploaded}
        onClick={() => {
          setShowModal(true);
        }}
        startIcon={<RenameSvg color={theme.palette.primary.contrastText} />}
      >
        {t("common:button.rename")}
      </PillButton>
      {showModal ? <RenameModal name={name} onUpdate={handleRename} open={showModal} onClose={() => setShowModal(false)} /> : <></>}
    </Fragment>
  );
};
